import { geojsonToArcGIS } from '@terraformer/arcgis';
import CIMSymbol from '@arcgis/core/symbols/CIMSymbol';

import type {
  FeatureWithIDs
} from '@/overmind/effects/adapters/baseadapter';

import powerTransformerSvg from '../Calculation/assets/01_Substation.svg';
import externalNetworkInjectionSvg from '../Calculation/assets/externalNetworkInjection_withBackground.svg';

import Graphic from '@arcgis/core/Graphic';

import PictureMarkerSymbol from '@arcgis/core/symbols/PictureMarkerSymbol';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';

import Point from '@arcgis/core/geometry/Point.js';
import Polyline from '@arcgis/core/geometry/Polyline.js';
import Polygon from '@arcgis/core/geometry/Polygon.js';
import { ColorStyle, HighlightStates, styleMapElementForAllCalculations } from '@/features/execute-calculation-set/components/Calculation/utils/styleMapElement';
import { isInitiatingGridElement, RenderedElement } from '@/features/execute-calculation-set/components/Calculation/utils/MapElementDesigner';
import { DomainGuid } from '@/features/execute-calculation-set/overmind/calculation';
import { createColorStyles } from '@/features/execute-calculation-set/components/Calculation/utils/createColorStyles';
import { getCenterLatLng } from '../Calculation/hooks/useMvTacticResultLayers';
import { IsGridElementFunction } from '../Calculation/hooks/useIsGridElementType';
import { getTransformerIconUrl } from '../Calculation/hooks/getIcons';

const colorStyles = createColorStyles({
  selectedElement: '#2ba3ab',
  subnet: '#6db7ae'
});

export function createArcGISGraphics(
  relatedObjectId: DomainGuid,
  features: FeatureWithIDs<unknown>[],
  externalNetworkInjectionId: DomainGuid | undefined,
  highlightedFeatures: HighlightStates,
  isGridElementType: IsGridElementFunction,
  useInitiatingObjectHighlighting: boolean
): Graphic[] {

  const additionalGraphicsRelatedWithFeatures: Graphic[] = [];
  const baseFeatureGraphics = features.map(feature => {
    const geometry = feature.geometry;
    const symbolStyle = styleMapElementForAllCalculations(useInitiatingObjectHighlighting)(
      RenderedElement.fromFeature(feature),
      undefined,
      relatedObjectId,
      highlightedFeatures,
      colorStyles,
      externalNetworkInjectionId
    );

    switch (geometry.type) {
      case 'Point':
        if (feature.gc.globalId === externalNetworkInjectionId) {
          return createPowerTransformerStartingPointGraphic(geometry, feature.properties, highlightedFeatures, feature.gc.globalId, relatedObjectId, useInitiatingObjectHighlighting);
        } else {
          if (isGridElementType("PowerTransformer", feature.gc.globalId)) {
            return createPowerTransformerGraphic(geometry, feature.properties, highlightedFeatures, feature.gc.globalId, relatedObjectId, useInitiatingObjectHighlighting);
          }
          return createPointGraphic(geometry, feature.properties, symbolStyle);
        }
      case 'LineString':
        if (feature.gc.globalId === externalNetworkInjectionId) {
          additionalGraphicsRelatedWithFeatures.push(createExternalNetworkInjectionGraphic(geometry, feature.properties));
        } else if (isGridElementType("Busbar", feature.gc.globalId)) {
          additionalGraphicsRelatedWithFeatures.push(createLineCenterPointGraphic(geometry, feature.properties, symbolStyle));
        }

        return createPolylineGraphic(geometry, feature.properties, symbolStyle, isInitiatingGridElement(relatedObjectId, feature.gc.globalId));
      case 'Polygon':
        return createPolygonGraphic(geometry, feature.properties, symbolStyle);
      default:
        throw new Error('Unknown geometry type');
    }
  });

  return baseFeatureGraphics.concat(additionalGraphicsRelatedWithFeatures);
}

export const createPolylineGraphic = (geometry: GeoJSON.Geometry, attributes: GeoJSON.GeoJsonProperties, symbolStyle: ColorStyle | undefined, isInitiatingGridElement: boolean): Graphic => {
  const symbol = getSymbol(symbolStyle, isInitiatingGridElement);
  return new Graphic({
    geometry: new Polyline(geojsonToArcGIS(geometry)),
    symbol: symbol,
    attributes
  });
}

export const createPolygonGraphic = (geometry: GeoJSON.Geometry, attributes: GeoJSON.GeoJsonProperties, symbolStyle: ColorStyle | undefined): Graphic => {
  return new Graphic({
    geometry: new Polygon(geojsonToArcGIS(geometry)),
    symbol: new SimpleFillSymbol({...symbolStyle}),
    attributes
  });
}

export const createPowerTransformerStartingPointGraphic = (
  geometry: GeoJSON.Geometry,
  attributes: GeoJSON.GeoJsonProperties,
  highlightedFeatures: HighlightStates,
  powerTransformerId: DomainGuid,
  initiatingElementId: DomainGuid,
  useInitiatingObjectHighlighting: boolean
): Graphic => {
  const svgUrl = getTransformerIconUrl(powerTransformerId, undefined, initiatingElementId, highlightedFeatures, useInitiatingObjectHighlighting)
  return new Graphic({
    geometry: new Point(geojsonToArcGIS(geometry)),
    symbol: new PictureMarkerSymbol(
      {
        url: svgUrl,
        width: 20,
        height: 20
      }),
    attributes
  });
}

export const createPowerTransformerGraphic = (
  geometry: GeoJSON.Geometry,
  attributes: GeoJSON.GeoJsonProperties,
  highlightedFeatures: HighlightStates,
  powerTransformerId: DomainGuid,
  initiatingElementId: DomainGuid,
  useInitiatingObjectHighlighting: boolean
): Graphic => {
  const svgUrl = getTransformerIconUrl(powerTransformerId, undefined, initiatingElementId, highlightedFeatures, useInitiatingObjectHighlighting)
  return new Graphic({
    geometry: new Point(geojsonToArcGIS(geometry)),
    symbol: new PictureMarkerSymbol(
      {
        url: svgUrl,
        width: 20,
        height: 20
      }),
    attributes
  });
}

export const createExternalNetworkInjectionGraphic = (geometry: GeoJSON.LineString, attributes: GeoJSON.GeoJsonProperties): Graphic => {
  const latlng = getCenterLatLng(
        geometry.coordinates
      );
  return new Graphic({
    geometry: new Point({...geojsonToArcGIS(geometry), latitude: latlng.lat, longitude: latlng.lng}),
    symbol: new PictureMarkerSymbol(
      {
        url: externalNetworkInjectionSvg,
        width: 20,
        height: 20
      }),
    attributes,
  });
}

export const createLineCenterPointGraphic = (geometry: GeoJSON.LineString, attributes: GeoJSON.GeoJsonProperties, symbolStyle: ColorStyle | undefined): Graphic => {
  const latlng = getCenterLatLng(
    geometry.coordinates
  );
  return new Graphic({
    geometry: new Point({...geojsonToArcGIS(geometry), latitude: latlng.lat, longitude: latlng.lng}),
    symbol: new SimpleMarkerSymbol({
      color: symbolStyle?.fillColor,
      outline: {
        color: "#FFFFFF",
        width: 1
      },
      size: (symbolStyle?.radius ?? 5) * 1.5,
    }),
    attributes
  });
}

function getSymbol(symbolStyle: ColorStyle, isInitiatingGridElement: boolean) {
  if(isInitiatingGridElement) {
    const mainLine = {
      type: 'CIMSolidStroke',
      enable: true,
      width: 5,
      color: hexToRgbaArray(symbolStyle?.color, 1)
    } as unknown as __esri.CIMSolidStroke;
    const borderLine = {
      type: 'CIMSolidStroke',
      enable: true,
      width: 9,
      color: [0, 0, 0, 255]
    } as unknown as __esri.CIMSolidStroke;

    return new CIMSymbol({
      data: {
        type: 'CIMSymbolReference',
        symbol: {
          type: 'CIMLineSymbol',
          symbolLayers: [
            mainLine,
            borderLine
          ]
        }
      }
    });
  }

  const anyLine = new SimpleLineSymbol({
    ...symbolStyle,
    width: symbolStyle?.weight,})

  return anyLine;
}

function hexToRgbaArray(
  hex: string,
  alpha: number
): [number, number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const a = Math.round(alpha * 255);

  return [r, g, b, a];
}

export const createPointGraphic = (geometry: GeoJSON.Geometry, attributes: GeoJSON.GeoJsonProperties, symbolStyle: ColorStyle | undefined): Graphic => {
  return new Graphic({
    geometry: new Point(geojsonToArcGIS(geometry)),
    symbol: new SimpleMarkerSymbol({
      color: symbolStyle?.fillColor,
      outline: symbolStyle,
      size: (symbolStyle?.radius ?? 5) * 1.5,
    }),
    attributes
  });
}

